/**
 * TasksSection — a brief the team can act on without a translation step.
 *
 * The demo's tasks said things like "исправить регион в 3 кампаниях" without
 * naming them, which turns a ready brief back into a research job. Every task
 * here lists its campaigns, and they survive into the clipboard and the
 * printable checklist — those are the forms the task actually travels in.
 *
 * Priorities carry their own deadline convention: P1 three days, P2 a week,
 * P3 two weeks. Stated once, at the top, so the copy of the list means
 * something on its own.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {ChipMUI} from 'ui/components/atoms/ChipMUI';
import {Button} from 'ui/components/atoms/Button';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {MEASURE, DAYS, pluralize} from '../helpers';
import {stripTypography} from '../typograph';
import {PRIORITY_DAYS, PRIORITY_LABEL} from '../checklist';
import type {PaletteTone} from '../content';
import type {Task, TaskPriority} from '../types';
import {CopyButton} from './CopyButton';
import {SplitCard, SplitPanel} from './SplitCard';

export interface TasksSectionProps {
  tasks: Task[];
  onPrint?: () => void;
}

/** Срок задачи по приоритету. Живёт рядом с документом, который его печатает. */
export {PRIORITY_DAYS} from '../checklist';

/** Приоритет по-русски: «P1» ничего не говорит человеку вне команды. */
export {PRIORITY_LABEL} from '../checklist';

const PRIORITY_TONE: Record<TaskPriority, PaletteTone> = {P1: 'bad', P2: 'alert', P3: 'good'};

const Title = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
  maxWidth: MEASURE,
});

const Description = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

const CampaignsLabel = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  maxWidth: MEASURE,
});

const Hint = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
  maxWidth: MEASURE,
});

/**
 * The clipboard form — carries the campaign names, which is the whole point.
 *
 * Служебные символы отсюда вычищаются: список уезжает в трекер или в
 * письмо, где наша типографика не нужна, а невидимый клей внутри слова там
 * ломает поиск по строке.
 */
export function tasksToText(tasks: Task[]): string {
  return tasks
    .map((task, index) => {
      const named = (task.campaigns ?? []).filter(campaign => campaign.name);
      const campaigns = named.length
        ? `\n   Кампании: ${named.map(campaign => campaign.name).join('; ')}`
        : '';
      const due = pluralize(PRIORITY_DAYS[task.priority], DAYS);
      return stripTypography(
        `${index + 1}. [${task.priority} · срок ${due}] ${task.title}\n   ${task.description}${campaigns}`,
      );
    })
    .join('\n\n');
}

export function TasksSection({tasks, onPrint}: TasksSectionProps) {
  return (
    <Stack gap='s'>
      <Hint>
        Готовый список для агентства или штатной команды. Сроки: {PRIORITY_LABEL.P1} —{' '}
        {pluralize(PRIORITY_DAYS.P1, DAYS)}, {PRIORITY_LABEL.P2} —{' '}
        {pluralize(PRIORITY_DAYS.P2, DAYS)}, {PRIORITY_LABEL.P3} —{' '}
        {pluralize(PRIORITY_DAYS.P3, DAYS)}.
      </Hint>

      {/* Задачи — один список, а не россыпь карточек: общий радиус на группе,
          между строками шов. Внутри строки контенту дано дышать. */}
      <SplitCard reflowAt='never' direction='column'>
        {tasks.map(task => (
          <SplitPanel key={task.title} padding='l'>
            <Row gap='m' alignY='start' reflowAt='md'>
              <ChipMUI mode='badge' color={PRIORITY_TONE[task.priority]} variant='soft' size='s'>
                {PRIORITY_LABEL[task.priority]}
              </ChipMUI>
              <Stack gap='s'>
                <Title>{task.title}</Title>
                <Description>{task.description}</Description>
                {(task.campaigns ?? []).some(campaign => campaign.name) && (
                  <Stack gap='xs'>
                    <CampaignsLabel>Кампании:</CampaignsLabel>
                    <Row gap='xs' wrap='wrap'>
                      {(task.campaigns ?? [])
                        .filter(campaign => campaign.name)
                        .map(campaign => (
                        <ChipMUI
                          key={campaign.campaignId}
                          mode='badge'
                          color='neutral'
                          variant='outline'
                          size='s'
                        >
                          {campaign.name}
                        </ChipMUI>
                      ))}
                    </Row>
                  </Stack>
                )}
              </Stack>
            </Row>
          </SplitPanel>
        ))}
      </SplitCard>

      <Row gap='s' justify='end' wrap='wrap'>
        <CopyButton
          value={tasksToText(tasks)}
          label='Скопировать список задач'
          size='m'
          variant='outline'
          color='primary'
        />
        <Button size='m' variant='solid' color='good' leftIcon='download' onClick={onPrint}>
          Скачать чек-лист
        </Button>
      </Row>
    </Stack>
  );
}
